import { HexEdge } from '@hexcrawl/shared-utils';
import { MapRenderer } from './MapRenderer';
import Graphics = Phaser.GameObjects.Graphics;

// These must match the tile geometry constants in MapRenderer.ts
const TILE_WIDTH = 512;
const TILE_HEIGHT = 591;
const HEX_SIDE_LENGTH = 294;

// Hex vertex offsets relative to the hex center, derived from tile geometry.
// The six corners of a pointy-top hex in clockwise order starting from the top:
//   top, upper-right, lower-right, bottom, lower-left, upper-left
const HALF_W = TILE_WIDTH / 2;
const HALF_H = TILE_HEIGHT / 2;
const HEX_INSET_Y = (TILE_HEIGHT - HEX_SIDE_LENGTH) / 2;  // 148.5 — distance from top/bottom to the angled vertices
const UPPER_Y = HEX_INSET_Y - HALF_H;   // -147 — y-offset of upper-left/upper-right vertices from center
const LOWER_Y = HALF_H - HEX_INSET_Y;   //  147 — y-offset of lower-left/lower-right vertices from center

// Per-edge vertex offsets: [x1, y1, x2, y2] relative to hex center
type EdgeOffsets = [number, number, number, number];
const EDGE_OFFSETS: Readonly<Record<number, EdgeOffsets>> = {
  [HexEdge.NW]: [-HALF_W, UPPER_Y,   0,      -HALF_H ],
  [HexEdge.NE]: [ 0,      -HALF_H,   HALF_W,  UPPER_Y],
  [HexEdge.E]:  [ HALF_W,  UPPER_Y,  HALF_W,  LOWER_Y],
  [HexEdge.SE]: [ HALF_W,  LOWER_Y,  0,        HALF_H ],
  [HexEdge.SW]: [ 0,        HALF_H, -HALF_W,  LOWER_Y],
  [HexEdge.W]:  [-HALF_W,  LOWER_Y, -HALF_W,  UPPER_Y],
};

const EDGE_ORDER = [HexEdge.NW, HexEdge.NE, HexEdge.E, HexEdge.SE, HexEdge.SW, HexEdge.W] as const;

const BORDER_COLOR = 0xffd700;
const BORDER_ALPHA = 0.9;
const BORDER_LINE_WIDTH = 12;
const BORDER_DEPTH = 8000;

// Returns the (dx, dy) offset to the neighbor in the given direction, using odd-r staggered hex grid rules.
function getNeighborOffset(y: number, edge: HexEdge): [number, number] {
  const odd = y % 2 === 1;
  switch (edge) {
    case HexEdge.NW: return odd ? [0, -1] : [-1, -1];
    case HexEdge.NE: return odd ? [1, -1] : [0, -1];
    case HexEdge.E:  return [1, 0];
    case HexEdge.SE: return odd ? [1, 1] : [0, 1];
    case HexEdge.SW: return odd ? [0, 1] : [-1, 1];
    case HexEdge.W:  return [-1, 0];
    default:         return [0, 0];
  }
}

export class RegionBorderRenderer {
  private _graphics?: Graphics;

  render(renderer: MapRenderer, regions: Array<Array<{ x: number; y: number }>>) {
    if (!this._graphics) {
      this._graphics = renderer.scene.add.graphics();
      this._graphics.setDepth(BORDER_DEPTH);
    }

    this._graphics.clear();

    if (regions.length === 0) return;

    this._graphics.lineStyle(BORDER_LINE_WIDTH, BORDER_COLOR, BORDER_ALPHA);

    for (const hexes of regions) {
      const hexSet = new Set(hexes.map(h => `${h.x},${h.y}`));

      for (const { x, y } of hexes) {
        const center = renderer.hexToWorld(x, y);
        if (!center) continue;

        for (const edge of EDGE_ORDER) {
          const [dx, dy] = getNeighborOffset(y, edge);
          if (hexSet.has(`${x + dx},${y + dy}`)) continue;

          const [ox1, oy1, ox2, oy2] = EDGE_OFFSETS[edge];
          this._graphics.lineBetween(
            center.x + ox1, center.y + oy1,
            center.x + ox2, center.y + oy2,
          );
        }
      }
    }
  }
}
